/**
 * @fileoverview World Organization V3 schema definitions for factions, nations, and institutions.
 *
 * This file contains:
 * - Canonical field definitions for world organizations (single source of truth)
 * - Dynamically generated Zod schemas with type-safe casting
 * - All organization-related schemas (entity, snapshot, idea, context variants)
 *
 * Pattern follows the character domain (character-sub-entities-v2.ts) and world-event.ts.
 */

import { z } from 'zod';
import { SHARED_WORLD_ELEMENT_FIELDS, type WorldElementFieldDefinition } from './shared-fields';

// =============================================================================
// CANONICAL FIELD DEFINITIONS - SINGLE SOURCE OF TRUTH
// =============================================================================

/**
 * Organization-specific field definitions.
 * Combined with shared fields, these define all attributes of a world organization.
 */
const ORGANIZATION_SPECIFIC_FIELDS: WorldElementFieldDefinition[] = [
  {
    key: 'leadership',
    label: 'Leadership',
    schemaDescription: "Who leads and how power is structured",
    initialStateDescription: "Who leads this organization and how authority is distributed at the start of the story.",
    journeyDescription: "Current leadership and any changes in power structure or succession.",
  },
  {
    key: 'membership',
    label: 'Membership & Structure',
    schemaDescription: "Who belongs and how the organization is organized",
    initialStateDescription: "Who belongs to this organization and how it is structured at the start of the story.",
    journeyDescription: "Current membership and any changes in organizational structure or notable members.",
  },
  {
    key: 'goals',
    label: 'Goals & Objectives',
    schemaDescription: "What the organization seeks to achieve",
    initialStateDescription: "What this organization is working toward at the start of the story.",
    journeyDescription: "Current goals and any shifts in objectives or newly revealed agendas.",
  },
  {
    key: 'resources',
    label: 'Resources & Assets',
    schemaDescription: "What the organization controls or can deploy",
    initialStateDescription: "What resources, wealth, or assets this organization controls at the start of the story.",
    journeyDescription: "Current resources and any gains, losses, or newly revealed assets.",
  },
  {
    key: 'territory',
    label: 'Territory & Influence',
    schemaDescription: "Geographic or social areas of control",
    initialStateDescription: "The geographic or social areas this organization controls or influences at the start of the story.",
    journeyDescription: "Current territory and any expansion, contraction, or shifts in influence.",
  },
  {
    key: 'relationships',
    label: 'Alliances & Enemies',
    schemaDescription: "Relationships with other organizations",
    initialStateDescription: "This organization's alliances, rivalries, and relationships with other groups at the start of the story.",
    journeyDescription: "Current alliances and enmities and any newly formed or broken relationships.",
  },
  {
    key: 'ideology',
    label: 'Ideology & Values',
    schemaDescription: "Core beliefs and principles",
    initialStateDescription: "The core beliefs, principles, and values that guide this organization at the start of the story.",
    journeyDescription: "Current ideology and any shifts in beliefs, schisms, or newly revealed principles.",
  },
  {
    key: 'history',
    label: 'Origin & History',
    schemaDescription: "How the organization came to be",
    initialStateDescription: "How this organization was founded and its history up to the start of the story.",
    journeyDescription: "Known history and any newly revealed origins, past events, or forgotten chapters.",
  },
];

/**
 * Complete field definitions for world organizations.
 * Combines shared fields with organization-specific fields.
 */
export const WORLD_ORGANIZATION_FIELD_DEFINITIONS: readonly WorldElementFieldDefinition[] = [
  ...SHARED_WORLD_ELEMENT_FIELDS,
  ...ORGANIZATION_SPECIFIC_FIELDS,
] as const;

// =============================================================================
// ENUMS
// =============================================================================

/**
 * Element subtypes for world organizations.
 * - faction: Secret societies, guilds, informal groups (The Thieves' Guild, House Martell, The Resistance)
 * - nation: Countries, empires, kingdoms, city-states (Kingdom of Valdris, The Northern Alliance)
 * - institution: Formal organizations, schools, churches (The Academy, The Church of Light, Harvard University)
 */
export const WorldOrganizationElementEnum = z.enum(['faction', 'nation', 'institution']);
export type WorldOrganizationElement = z.infer<typeof WorldOrganizationElementEnum>;

// =============================================================================
// DYNAMICALLY GENERATED SCHEMAS (following character domain pattern)
// =============================================================================

/**
 * Builds the base world organization attributes schema from the canonical field definitions.
 * All fields are optional for world organizations.
 */
const buildAttributesSchema = () => {
  const schemaShape = WORLD_ORGANIZATION_FIELD_DEFINITIONS.reduce((acc, field) => {
    acc[field.key] = z.string().optional().describe(field.schemaDescription);
    return acc;
  }, {} as Record<string, z.ZodOptional<z.ZodString>>);

  // Type cast to preserve TypeScript inference (must be kept in sync with field definitions)
  return z.object(schemaShape as {
    // Shared fields
    relevanceToPlot: z.ZodOptional<z.ZodString>;
    currentState: z.ZodOptional<z.ZodString>;
    knownInformation: z.ZodOptional<z.ZodString>;
    // Organization-specific fields
    leadership: z.ZodOptional<z.ZodString>;
    membership: z.ZodOptional<z.ZodString>;
    goals: z.ZodOptional<z.ZodString>;
    resources: z.ZodOptional<z.ZodString>;
    territory: z.ZodOptional<z.ZodString>;
    relationships: z.ZodOptional<z.ZodString>;
    ideology: z.ZodOptional<z.ZodString>;
    history: z.ZodOptional<z.ZodString>;
  });
};

/**
 * The base schema for world organization attributes, used by agents that generate or extract
 * organization details. All attributes are optional.
 */
export const WorldOrganizationAttributesV3Schema = buildAttributesSchema();

// =============================================================================
// ENTITY SCHEMAS
// =============================================================================

/**
 * A point-in-time snapshot of a world organization's state at the end of a specific chapter.
 * Extends attributes with temporal metadata.
 */
export const WorldOrganizationSnapshotV3Schema = WorldOrganizationAttributesV3Schema.extend({
  id: z.string().describe("Unique identifier for this snapshot (typically matches chapterId)."),
  chapterId: z.string().describe("ID of the chapter this snapshot corresponds to."),
  chapterNumber: z.number().describe("The number of the chapter."),
  timestamp: z.string().describe("ISO timestamp of when this snapshot was created."),
  notPresentInChapter: z.boolean().optional().describe("Flag indicating this is a carry-forward snapshot."),
});

/**
 * The canonical World Organization entity.
 * A container for metadata and a timeline of snapshots.
 */
export const WorldOrganizationV3Schema = z.object({
  id: z.string().describe("Unique identifier for the world organization."),
  name: z.string().describe("The name of the organization."),
  type: z.literal('organization').describe("Discriminator for world element type."),
  element: WorldOrganizationElementEnum.describe("The subtype of organization (faction, nation, or institution)."),
  initialDescription: z.string().optional().describe("Brief description of the organization before detailed generation."),
  snapshots: z.array(WorldOrganizationSnapshotV3Schema).describe("Complete timeline of organization state across chapters."),
  latestSnapshotId: z.string().optional().describe("Quick reference to the most recent snapshot."),
  notes: z.string().optional().describe("User notes about this organization."),
  mainImageId: z.string().optional().describe("Reference to the main image for this organization."),
});

// =============================================================================
// IDEATION SCHEMAS
// =============================================================================

/**
 * Schema for a world organization idea before it's added to the project.
 * Used during AI ideation and extraction flows.
 */
export const WorldOrganizationIdeaV3Schema = z.object({
  tempId: z.string().describe("Temporary ID for UI tracking before persistence."),
  name: z.string().describe("The proposed name of the organization."),
  element: WorldOrganizationElementEnum.describe("The proposed subtype (faction, nation, or institution)."),
  initialDescription: z.string().describe("Brief description of the organization concept."),
  source: z.enum(['ideation', 'extraction', 'manual']).describe("How this idea was generated."),
});

// =============================================================================
// CONTEXT SCHEMAS (for AI agents)
// =============================================================================

/**
 * Minimal reference schema for world organizations.
 * Used when only ID and name are needed.
 */
export const WorldOrganizationRefV3Schema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('organization'),
});

/**
 * Basic context schema for world organizations.
 * Used for high-level summaries in agent context.
 */
export const WorldOrganizationBasicV3Schema = z.object({
  name: z.string(),
  type: z.literal('organization'),
  element: WorldOrganizationElementEnum,
  initialDescription: z.string().optional(),
});

/**
 * Rich context schema for world organizations.
 * Includes the full entity plus the resolved latest snapshot.
 */
export const WorldOrganizationContextV3Schema = WorldOrganizationV3Schema.extend({
  latestSnapshot: WorldOrganizationSnapshotV3Schema.optional(),
});

// =============================================================================
// TYPE INFERENCE (single source of truth from Zod schemas)
// =============================================================================

export type WorldOrganizationAttributes = z.infer<typeof WorldOrganizationAttributesV3Schema>;
export type WorldOrganizationSnapshotV3 = z.infer<typeof WorldOrganizationSnapshotV3Schema>;
export type WorldOrganizationV3 = z.infer<typeof WorldOrganizationV3Schema>;
export type WorldOrganizationIdeaV3 = z.infer<typeof WorldOrganizationIdeaV3Schema>;
export type WorldOrganizationRefV3 = z.infer<typeof WorldOrganizationRefV3Schema>;
export type WorldOrganizationBasicV3 = z.infer<typeof WorldOrganizationBasicV3Schema>;
export type WorldOrganizationContextV3 = z.infer<typeof WorldOrganizationContextV3Schema>;
